import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';
import logger from '../utils/logger';


// Create the context
const TaskContext = createContext();

// Custom hook to use the task context
export const useTasks = () => {
  const context = useContext(TaskContext);
  if (!context) {
    throw new Error('useTasks must be used within a TaskProvider');
  }
  return context;
};

// Provider component
export const TaskProvider = ({ children }) => {
  // Initial dummy tasks 
  const initialTasks = [
    {
      _id: '1',
      title: 'Complete morning routine',
      description: 'Relaxation, exercise, and journaling',
      dueDate: new Date().toISOString(),
      completed: false
    },
    {
      _id: '2',
      title: 'Review weekly goals',
      description: 'Check progress on objectives',
      dueDate: new Date().toISOString(),
      completed: true
    },
    {
      _id: '3',
      title: 'Practice mindfulness',
      description: '15 minute mindfulness session',
      dueDate: new Date(Date.now() + 86400000).toISOString(), // Tomorrow
      completed: false
    }
  ];

  const [tasks, setTasks] = useState(initialTasks);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchTasks = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication required');
      }

      // Try to fetch from server, if fails, use dummy data
      try {
        const response = await axios.get('https://backend-rough-bird-115.fly.dev/api/tasks', {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        
        if (response.data && Array.isArray(response.data)) {
          setTasks(response.data);
        }
      } catch (err) {
        logger.error('Error fetching tasks from server, using dummy data:', err);
        // Keep using the initial tasks
        setTasks(initialTasks);
      }
    } catch (err) {
      logger.error('Authentication error:', err);
      setError('Failed to authenticate');
      setTasks(initialTasks);
    } finally {
      setLoading(false);
    }
  };

  const addTask = async (newTask) => {
    setLoading(true);
    setError(null);
    try {
      // Generate a temporary ID
      const tempId = Date.now().toString();
      const taskWithId = { 
        ...newTask, 
        _id: tempId,
        dueDate: newTask.dueDate || new Date().toISOString()
      };
      
      // Add to local state first
      setTasks([...tasks, taskWithId]);
      
      // Try to send to server if available
      const token = localStorage.getItem('token');
      if (token) {
        try {
          const response = await axios.post(
            'https://backend-rough-bird-115.fly.dev/api/tasks',
            newTask,
            {
              headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            }
          );
          
          // If server responded with the saved task, update the local state
          if (response.data && response.data._id) {
            setTasks(prevTasks => 
              prevTasks.map(task => 
                task._id === tempId ? response.data : task
              )
            );
          }
        } catch (err) {
          logger.error('Error sending task to server:', err);
          // Task is already added to local state, so we continue
        }
      }
      
      return taskWithId;
    } catch (err) {
      logger.error('Error adding task:', err);
      setError('Failed to add task');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updateTask = async (id, updatedTask) => {
    setLoading(true);
    setError(null);
    try {
      // Update in local state first
      setTasks(tasks.map(task => (task._id === id ? { ...task, ...updatedTask } : task)));
      
      // Try to update on server if available
      const token = localStorage.getItem('token');
      if (token) {
        try {
          await axios.put(
            `https://backend-rough-bird-115.fly.dev/api/tasks/${id}`,
            updatedTask,
            {
              headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            }
          );
        } catch (err) {
          logger.error('Error updating task on server:', err);
          // Task is already updated in local state, so we continue
        }
      }
      
      return { ...updatedTask, _id: id };
    } catch (err) {
      logger.error('Error updating task:', err);
      setError('Failed to update task');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const deleteTask = async (id) => {
    setLoading(true);
    setError(null);
    try {
      // Remove from local state first
      setTasks(tasks.filter(task => task._id !== id));
      
      // Try to delete from server if available
      const token = localStorage.getItem('token');
      if (token) {
        try {
          await axios.delete(`https://backend-rough-bird-115.fly.dev/api/tasks/${id}`, {
            headers: {
              Authorization: `Bearer ${token}`
            }
          });
        } catch (err) {
          logger.error('Error deleting task from server:', err);
          // Task is already deleted from local state, so we continue
        }
      }
    } catch (err) {
      logger.error('Error deleting task:', err);
      setError('Failed to delete task');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Value object that will be passed to consumers
  const value = {
    tasks,
    loading,
    error,
    fetchTasks,
    addTask,
    updateTask,
    deleteTask
  };

  return <TaskContext.Provider value={value}>{children}</TaskContext.Provider>;
};
